import React, {useMemo, useRef, useState} from "react";
import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {useQuery} from "@tanstack/react-query";
import SmartcaptureService from "../../../../services/web/smartcapture-service";
import LaneSelect from "../components/lane-select.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaAngleLeft, FaAngleRight, FaAnglesLeft, FaAnglesRight} from "react-icons/fa6";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useParams} from "react-router";
import MySwal from "sweetalert2";
import Swal from "sweetalert2";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {AgGridReact} from "ag-grid-react";
import projectUtils from "../../../../utils/project-utils.js";
import AlertMessage from "../../../../components/messages/alert-message.js";

const initialState = {
    name: "",
    ipExtern: "",
    maintenanceGroup: null,
    pin: ""
};

const SmartcaptureForm = () => {
    const { pin } = useParams();
    const navigate = navigateHook();

    const [data, setData] = useState(initialState);
    const [availableLanes, setAvailableLanes] = useState([]);
    const [selectedLanes, setSelectedLanes] = useState([]);

    const [selectedAvailable, setSelectedAvailable] = useState([]);
    const [selectedRelated, setSelectedRelated] = useState([]);

    const [lanesToMigrate, setLanesToMigrate] = useState([]);

    const gridRef = useRef(null);
    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    const { isLoading, refetch } = useQuery({
        queryKey: ['smartcapture', pin],
        enabled: !!pin,
        queryFn: () =>
            SmartcaptureService.getById(pin).then((data) => {
                setData(data);
                setAvailableLanes(data.availableLanes || []);
                setSelectedLanes(data.relatedLanes || []);
                return data;
            })
    });

    const { data: responsibleOptions } = useQuery({
        queryKey: ["maintenance-groups"],
        queryFn: () =>
            SmartcaptureService.getPclMaintenanceGroupsOptions().then((data) =>
                data.map((item) => ({
                    value: item.id,
                    label: String(item.text),
                }))
            ),
    });

    const columnDefs =  [
        { headerName: "Nome", field: "name", flex: 1, tooltipField: "name"},
        { headerName: "Smartcapture", field: "pin", width: 160, tooltipField: "pin"}
    ];

    const defaultColDef = useMemo(() => ({
        tooltipValueGetter: (params) => {
            if (params.value === null || params.value === undefined) return "";
            return String(params.value);
        },
        sortable: true,
        resizable: true,
    }), []);

    const addToMigrateList = (lanes) => {
        setLanesToMigrate(prev => {
            const existingIds = prev.map(p => p.code);

            const newItems = lanes
                .filter(l => l.smartCapturePin)
                .filter(l => !existingIds.includes(l.id))
                .map(l => ({
                    code: l.id,
                    pin: l.smartCapturePin,
                    name: `${l.capturePlace} (${l.id} - ${l.route} ${l.laneObservation ?? ''})`
                }));

            return [...prev, ...newItems];
        });
    };

    const moveToSelected = (lanes) => {
        if (!lanes.length) return;

        setAvailableLanes(prev => prev.filter(l => !lanes.some(x => x.id === l.id)));
        setSelectedLanes(prev => [...prev, ...lanes]);

        addToMigrateList(lanes);
        setSelectedAvailable([]);
    };

    const moveToAvailable = (lanes) => {
        if (!lanes.length) return;

        setSelectedLanes(prev => prev.filter(l => !lanes.some(x => x.id === l.id)));
        setAvailableLanes(prev => [...prev, ...lanes]);

        setLanesToMigrate(prev =>
            prev.filter(m => !lanes.some(l => l.id === m.code))
        );

        setSelectedRelated([]);
    };

    const moveAllToSelected = () => moveToSelected(availableLanes);
    const moveAllToAvailable = () => moveToAvailable(selectedLanes);

    const handleSave = async (force = false, migrateNumber = null) => {

        if (!force && lanesToMigrate.length > 0) {
            const { value, isConfirmed } = await Swal.fire({
                title: "Atenção",
                html: `
                    Esta operação não pode ser cancelada!
                    Digite o número de câmeras que serão migradas:
                `,
                icon: "warning",
                input: "number",
                inputAttributes: {
                    min: 0
                },
                confirmButtonText: "Confirmar",
                confirmButtonColor: "#063A6C",
                cancelButtonText: "Cancelar",
                showCancelButton: true,
                reverseButtons: true,
                preConfirm: (value) => {
                    if (!value) {
                        Swal.showValidationMessage("O campo número de câmeras é obrigatório.");
                        return false;
                    }

                    return value;
                }
            });

            if (!isConfirmed) return;

            return handleSave(true, Number(value));
        }

        let response;
        response = SmartcaptureService.update({
            pin: data.pin,
            name: data.name,
            ipExtern: data.ipExtern,
            lanes: selectedLanes.map(l => l.id),
            maintenanceGroupsId: data.maintenanceGroup?.value ?? data.maintenanceGroup ?? null,
            lanesToMigrate: migrateNumber
        });

        response
            .then(() => {
                projectUtils.success();

                navigate("/noc-smartcaptures");
            })
            .catch((error) => {
                console.error(error);

                const message = error.response?.data;
                let errMessage = AlertMessage[message] ?? "Não foi possível salvar.";

                projectUtils.swalErrorMessage("", errMessage, "Ok");
            });
    };

    const handleDelete = async () => {
        const { value: pinConfirmation, isConfirmed } = await MySwal.fire({
            title: "Confirmação",
            text: "Atenção, esta operação apagará todos os dados de SLA referente a esse smartcapture! Para continuar, insira o PIN.",
            input: "password",
            showCancelButton: true,
            cancelButtonText: "Cancelar",
            confirmButtonText: "Excluir",
            confirmButtonColor: "#cb0000",
            reverseButtons: true
        });

        if (!isConfirmed) return;

        const formData = new FormData();
        formData.append("pin", data.pin);
        formData.append("pinConfirmation", pinConfirmation);

        try {
            await SmartcaptureService.delete(formData);
            projectUtils.success(true, "Excluído com sucesso");
            navigate("/noc-smartcaptures");
        } catch (error) {
            console.error(error);

            const message = error.response?.data;

            let errMessage = AlertMessage[message] ?? "Não foi possível salvar.";
            projectUtils.swalErrorMessage("", errMessage, "Ok");
        }
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Smartcapture</Template.Title>
            </Template.Header>

            <Template.Body className="h-full flex flex-col gap-2 min-h-0">
                <Card className="flex flex-col">
                    <Card.Body className="flex flex-col h-full">
                        <div className="grid grid-cols-12 gap-4">
                            <InputForm
                                label="Nome"
                                value={data.name}
                                onChange={e => setData(prev => ({ ...prev, name: e.target.value }))}
                                classNameWrapper="col-span-6"
                            />

                            <SelectComponent
                                label="Resp. por manutenção"
                                options={responsibleOptions}
                                value={data.maintenanceGroup}
                                onChange={value => setData(prev => ({ ...prev, maintenanceGroup: value }))}
                                classNameWrapper="col-span-3"
                            />

                            <InputForm
                                label="Acesso remoto"
                                value={data.ipExtern}
                                onChange={e => setData(prev => ({ ...prev, ipExtern: e.target.value }))}
                                classNameWrapper="col-span-3"
                            />
                        </div>
                    </Card.Body>
                </Card>


                <div className="flex flex-1 gap-2 min-h-0">
                    <LaneSelect
                        label="Câmeras disponíveis"
                        lanes={availableLanes}
                        onSelectionChange={setSelectedAvailable}
                    />

                    <div className="flex flex-col justify-center items-center gap-2">
                        <Button target="default" className="w-[32px] !p-0" onClick={moveAllToSelected}><FaAnglesRight /></Button>
                        <Button target="default" className="w-[32px] !p-0" onClick={() => moveToSelected(selectedAvailable)}><FaAngleRight /></Button>
                        <Button target="default" className="w-[32px] !p-0" onClick={() => moveToAvailable(selectedRelated)}><FaAngleLeft /></Button>
                        <Button target="default" className="w-[32px] !p-0" onClick={moveAllToAvailable}><FaAnglesLeft /></Button>
                    </div>

                    <LaneSelect
                        label="Câmeras selecionadas"
                        lanes={selectedLanes}
                        onSelectionChange={setSelectedRelated}
                    />
                </div>

                { lanesToMigrate.length > 0 && (
                    <Card className="flex flex-col min-h-0">
                        <Card.Header>
                            <Card.Title>Câmeras que serão migradas para este Smartcapture:</Card.Title>
                        </Card.Header>

                        <Card.Body className="flex flex-col flex-1 min-h-50">
                            <div className="ag-theme-alpine w-full h-[300px]">
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={lanesToMigrate}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    suppressCellFocus
                                    animateRows={false}
                                    gridOptions={{ localeText: aggridUtils.localization() }}
                                    theme={theme}
                                />
                            </div>

                            <div className="text-sm mt-2">
                                <b>Total:</b> {lanesToMigrate.length}
                            </div>
                        </Card.Body>
                    </Card>
                )}

                <Card>
                    <Card.Body className="flex justify-end gap-2">
                        <Button target="danger" onClick={handleDelete} actionFeedbackType='delete'>Excluir</Button>
                        <Button target="primary" onClick={() => handleSave(false)} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default SmartcaptureForm;